import { ReturnRequestLogDto } from "@babackapp/private-api-client/types";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Button } from "@babackapp/ui/components/button";
import { LightningBoltIcon, ReloadIcon } from "@radix-ui/react-icons";
import ActivityWrapper from "./activity-wrapper";
import ActivityIconWrapper from "./activity-icon-wrapper";
import ActivityContentWrapper from "./activity-content-wrapper";
import { ActivityItemHeader } from "./activity-item-header";
import useTranslation, {
  Translation,
} from "@babackapp/translations/use-translation";
import useReplayWebhook from "@babackapp/private-api-client/client/use-replay-webhook";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";

type WebhookActivityProps = {
  activity: ReturnRequestLogDto;
  returnRequestId: string;
};

export default function WebhookActivity({
  activity,
  returnRequestId,
}: WebhookActivityProps) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const replayWebhook = useReplayWebhook(shop?.id);

  const params = activity.params as {
    url: string;
    action: string;
    status: string;
    error?: string;
    response?: any;
    webhookId?: string;
  };
  const isSuccess = params.status === "success";

  const handleReplay = async () => {
    try {
      if (replayWebhook.isPending) return;
      await replayWebhook.mutateAsync({
        returnRequestId,
        action: activity.params.action as string,
      });
    } catch (error) {
      console.error("Erreur lors du replay du webhook:", error);
    }
  };
  return (
    <ActivityWrapper>
      <ActivityIconWrapper
        className={`${isSuccess ? "" : "text-red-500 bg-red-100"}`}
      >
        <LightningBoltIcon className={`size-4 `} />
      </ActivityIconWrapper>
      <ActivityContentWrapper>
        <div className="flex items-center gap-2 flex-1 justify-between w-full min-w-0">
          <Accordion
            type="single"
            collapsible
            className="flex-1 min-w-0 w-full"
          >
            <AccordionItem value="item-1" className="border-0">
              <AccordionTrigger className="p-0 w-full min-w-0">
                <ActivityItemHeader
                  title=""
                  timestamp={activity.date}
                  description={
                    <div className="w-full min-w-0 max-w-full">
                      <div className="w-full min-w-0 overflow-hidden max-w-[500px]">
                        <span
                          className="block overflow-hidden text-ellipsis whitespace-nowrap text-muted-foreground font-light font-stretch-90% text-xs w-full"
                          title={params.url}
                        >
                          {params.url}
                        </span>
                      </div>
                    </div>
                  }
                >
                  <div className="flex flex-col gap-0">
                    <div className="flex items-center gap-2">
                      <span>Webhook</span>
                      <span>
                        {t(
                          `settings.integrations.webhooks.${params.action}` as Translation,
                          {
                            keySeparator: ".",
                            nsSeparator: ".",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                </ActivityItemHeader>
              </AccordionTrigger>
              <AccordionContent>
                {params.action && (
                  <Button
                    variant="link"
                    size="sm"
                    onClick={handleReplay}
                    disabled={replayWebhook.isPending}
                    className={`
                      flex items-center gap-1 text-xs mt-2 px-0
                      ${replayWebhook.isPending ? "pointer-events-none opacity-60" : ""}
                    `}
                    tabIndex={0}
                  >
                    <ReloadIcon
                      className={`h-4 w-4 ${replayWebhook.isPending ? "animate-spin" : ""}`}
                    />
                    {t("activities.webhook.replay")}
                  </Button>
                )}
                {params.error && (
                  <div className="text-sm text-red-600 mt-2">
                    <span className="font-medium">Erreur : </span>
                    {params.error}
                  </div>
                )}
                {params.response && (
                  <pre className="mt-2 rounded-md bg-muted p-4 text-xs overflow-x-auto whitespace-pre-wrap">
                    {typeof params.response === "string"
                      ? params.response
                      : JSON.stringify(params.response, null, 2)}
                  </pre>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </ActivityContentWrapper>
    </ActivityWrapper>
  );
}
